import { useMemo, useRef } from 'react';

import { useTranslation } from '@kc-console/core';

import { DEFAULT_DATE_FORMAT, DEFAULT_DATE_MASK, DEFAULT_DATE_RANGE_MASK } from '../constants';
import { generateMaskFromDateFormat, getLocale } from '../utils';

import type ReactDatePicker from 'react-datepicker';
import type { Locale } from 'date-fns';
import type { PickerConfig } from '../core/PickerConfig';
import type { DatePickerProps, PeriodPickerProps } from '../types';

export interface UseDatePickerOutput {
  dateFormat: string;
  datePickerRef: React.RefObject<ReactDatePicker | null>;
  locale: Locale;
  mask: string;
}

interface UseDatePickerOptions {
  props: DatePickerProps | PeriodPickerProps;
  pickerConfig?: PickerConfig;
}

export const useDatePicker = ({
  props,
  pickerConfig,
}: UseDatePickerOptions): UseDatePickerOutput => {
  const { locale: propLocale = 'ko', dateFormat, selectsRange = false, inputProps } = props;
  const showTime = 'showTime' in props ? props.showTime : false;
  const showSeconds = 'showSeconds' in props ? (props.showSeconds ?? true) : true;

  const { t } = useTranslation();
  const datePickerRef = useRef<ReactDatePicker>(null);
  const locale = useMemo(() => getLocale(propLocale), [propLocale]);

  const finalDateFormat = useMemo(() => {
    if (dateFormat) {
      return dateFormat;
    }

    if (showTime) {
      return showSeconds
        ? t('ui:datepicker.format.datetime')
        : t('ui:datepicker.format.datetime_no_seconds');
    }

    return pickerConfig?.dateFormat ?? DEFAULT_DATE_FORMAT;
  }, [dateFormat, pickerConfig?.dateFormat, showTime, showSeconds, t]);

  const finalMask = useMemo(() => {
    if (inputProps?.mask) {
      return inputProps.mask;
    }

    if (dateFormat) {
      return generateMaskFromDateFormat(dateFormat);
    }

    if (showTime) {
      return showSeconds
        ? t('ui:datepicker.mask.datetime')
        : t('ui:datepicker.mask.datetime_no_seconds');
    }

    if (pickerConfig) {
      return selectsRange ? `${pickerConfig.mask} ~ ${pickerConfig.mask}` : pickerConfig.mask;
    }

    if (selectsRange) {
      return DEFAULT_DATE_RANGE_MASK;
    }

    return DEFAULT_DATE_MASK;
  }, [inputProps?.mask, dateFormat, pickerConfig, showTime, showSeconds, selectsRange, t]);

  return {
    datePickerRef,
    locale,
    dateFormat: finalDateFormat,
    mask: finalMask,
  };
};
